import React, { useState } from 'react';
import { Modal, Table, Button, Space, Popconfirm, Form, Switch, Input, ConfigProvider } from 'antd';
import { EditOutlined, DeleteOutlined, PlusOutlined } from '@ant-design/icons';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { ApiCaller, Utils, Colors } from '../../../config';
import { getTimeOffTypes } from '../../../config/getApis';
import useColors from '../../../hooks/useColors';
import { Button as CustomButton, Switch as CustomSwitch } from '../../../components';

const TimeOffTypeManager = ({ visible, onClose }) => {
    const { colorPrimary } = useColors();
    const queryClient = useQueryClient();
    const [page, setPage] = useState(1);
    const [perPage, setPerPage] = useState(10);
    const [search, setSearch] = useState('');
    const [editingType, setEditingType] = useState(null);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [form] = Form.useForm();

    const { data, isLoading } = useQuery(
        ['timeOffTypes', page, perPage, search],
        () => getTimeOffTypes({ page, per_page: perPage, search }),
        {
            keepPreviousData: true,
            enabled: visible
        }
    );

    const createMutation = useMutation((payload) => ApiCaller.Post('/time-off-type', payload), {
        onSuccess: () => {
            Utils.showMessage('success', 'Type created successfully');
            queryClient.invalidateQueries('timeOffTypes');
            queryClient.invalidateQueries('allTimeOffTypes'); // Invalidate the dropdown list too if used elsewhere
            setIsModalOpen(false);
            form.resetFields();
        },
        onError: (err) => Utils.showMessage('error', err?.response?.data?.message || 'Failed to create type')
    });

    const updateMutation = useMutation(({ id, payload }) => ApiCaller.Put(`/time-off-type/${id}`, payload), {
        onSuccess: () => {
            Utils.showMessage('success', 'Type updated successfully');
            queryClient.invalidateQueries('timeOffTypes');
            queryClient.invalidateQueries('allTimeOffTypes');
            setIsModalOpen(false);
            setEditingType(null);
            form.resetFields();
        },
        onError: (err) => Utils.showMessage('error', err?.response?.data?.message || 'Failed to update type')
    });

    const deleteMutation = useMutation((id) => ApiCaller.Delete(`/time-off-type/${id}`), {
        onSuccess: () => {
            Utils.showMessage('success', 'Type deleted successfully');
            queryClient.invalidateQueries('timeOffTypes');
            queryClient.invalidateQueries('allTimeOffTypes');
        },
        onError: (err) => Utils.showMessage('error', err?.response?.data?.message || 'Failed to delete type')
    });

    const columns = [
        { title: 'Name', dataIndex: 'name', key: 'name' },
        { title: 'Description', dataIndex: 'description', key: 'description' },
        {
            title: 'Status',
            dataIndex: 'is_active',
            key: 'is_active',
            render: (active, record) => {

                const isMandatory = ['personal', 'sick', 'vacation'].includes(record.name.toLowerCase());
                if (isMandatory) return <span style={{ color: colorPrimary }}>Active</span>;
                return record?.isActive ? <span style={{ color: colorPrimary }}>Active</span> : <span style={{ color: 'red' }}>Inactive</span>;
            }
        },
        {
            title: 'Actions',
            key: 'actions',
            render: (_, record) => {
                const isMandatory = ['personal', 'sick', 'vacation'].includes(record.name.toLowerCase());
                return (
                    <Space>
                        {!isMandatory && (
                            <>
                                <Button icon={<EditOutlined />} onClick={() => handleEdit(record)} />
                                <Popconfirm title="Delete?" onConfirm={() => deleteMutation.mutate(record.id)}>
                                    <Button icon={<DeleteOutlined />} danger loading={deleteMutation.isLoading && deleteMutation.variables === record.id} />
                                </Popconfirm>
                            </>
                        )}
                        {isMandatory && <span style={{ color: 'gray', fontStyle: 'italic' }}>Mandatory</span>}
                    </Space>
                )
            }
        }
    ];

    const handleEdit = (record) => {
        setEditingType(record);
        form.setFieldsValue({
            name: record.name,
            description: record.description,
            is_active: record.isActive
        });
        setIsModalOpen(true);
    };

    const handleAdd = () => {
        setEditingType(null);
        form.resetFields();
        form.setFieldsValue({ is_active: true });
        setIsModalOpen(true);
    };

    const handleOk = async () => {
        try {
            const values = await form.validateFields();
            if (editingType) {
                updateMutation.mutate({ id: editingType.id, payload: values });
            } else {
                createMutation.mutate(values);
            }
        } catch (e) { }
    };

    return (
        <Modal
            title="Manage Time Off Types"
            open={visible}
            onCancel={onClose}
            footer={null}
            width={800}
        >
            <div className="flex justify-between mb-4">
                <Input
                    placeholder="Search..."
                    onChange={(e) => setSearch(e.target.value)}
                    className="w-1/2 py-2 rounded border-gray-300"
                />
                <CustomButton.Basic
                    type="primary"
                    icon={<PlusOutlined />}
                    onClick={handleAdd}
                    buttonStyle={{ backgroundColor: colorPrimary, color: 'white' }}
                    text="Add Type"
                    className="rounded-md h-9 px-4"
                />
            </div>

            <Table
                dataSource={data?.data || []}
                columns={columns}
                rowKey="id"
                loading={isLoading}
                pagination={{
                    current: page,
                    pageSize: perPage,
                    total: data?.paginate?.total || 0,
                    onChange: (p, ps) => { setPage(p); setPerPage(ps); }
                }}
            />

            <Modal
                title={editingType ? "Edit Type" : "Add Type"}
                open={isModalOpen}
                onOk={handleOk}
                onCancel={() => setIsModalOpen(false)}
                footer={[ // Custom footer using CustomButton.Basic
                    <div className="flex justify-end gap-2" key="footer">
                        <Button key="cancel" onClick={() => setIsModalOpen(false)}>
                            Cancel
                        </Button>
                        <CustomButton.Basic
                            key="submit"
                            loader={createMutation.isLoading || updateMutation.isLoading}
                            onClick={handleOk}
                            buttonStyle={{ backgroundColor: colorPrimary, color: 'white' }}
                            text={editingType ? "Update" : "Add"}
                            className="rounded-md h-8 px-4"
                        />
                    </div>
                ]}
            >
                <Form form={form} layout="vertical">
                    <Form.Item name="name" label="Name" rules={[{ required: true, message: 'Please enter a name' }]}>
                        <Input placeholder="Enter name (e.g., Bereavement)" />
                    </Form.Item>
                    <Form.Item name="description" label="Description">
                        <Input.TextArea placeholder="Enter description" />
                    </Form.Item>
                    <Form.Item name="is_active" label="Status" valuePropName="checked">
                        <StatusToggle colorPrimary={colorPrimary} />
                    </Form.Item>
                </Form>
            </Modal>
        </Modal>
    );
};

// Helper component for the switch with label
const StatusToggle = ({ checked, onChange, colorPrimary }) => (
    <div className="flex items-center gap-2">
        <Switch
            checked={checked}
            onChange={onChange}
            className="custom-switch"
            style={{ "--myColor": colorPrimary }}
        />
        <span className="font-medium" style={{ color: Colors.LabelColor }}>
            {checked ? 'Active' : 'In-Active'}
        </span>
    </div>
);

export default TimeOffTypeManager;
